/* Kookut product pages.
   - <kookut-product>: size, quantity and purchase type stay in sync with the Add to cart label and the mobile sticky bar.
     The subscription app (Joy) owns the purchase type controls; its selling_plan field is read from the form.
     Adding to cart goes through the theme's <product-form>, so the cart drawer behaves as elsewhere.
   - Gallery: swipe with dots on mobile, thumbnails on desktop.
   - Accordions: open columns on desktop, accordions on mobile.
   - "Add both to cart" for the Goes with section.
   - Recipe switcher: hovered recipe name. */

(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  const createMoneyFormatter = (sample) => {
    // sample is 123456 cents in the store's format, e.g. "CHF 1,234.56", "€1.234,56" or "1 234,56 €".
    const match = typeof sample === 'string' ? sample.match(/^(.*?)1(\D?)234(\D)56(.*)$/) : null;
    if (!match) return (cents) => (cents / 100).toFixed(2);
    const [, prefix, thousands, decimal, suffix] = match;
    return (cents) => {
      const whole = Math.floor(cents / 100)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const rest = cents % 100;
      return `${prefix}${whole}${rest ? decimal + String(rest).padStart(2, '0') : ''}${suffix}`;
    };
  };

  const fill = (template, values) =>
    Object.entries(values).reduce((text, [key, value]) => text.split(key).join(value), template);

  if (!customElements.get('kookut-product')) {
    customElements.define(
      'kookut-product',
      class KookutProduct extends HTMLElement {
        connectedCallback() {
          const dataEl = this.querySelector('[data-product-json]');
          if (!dataEl) return;
          this.data = JSON.parse(dataEl.textContent);
          this.formatMoney = createMoneyFormatter(this.data.moneySample);
          this.form = document.getElementById(this.dataset.formId);
          this.variantInput = this.querySelector('[data-variant-input]');
          this.qtyInput = this.querySelector('[data-qty-input]');
          this.addButton = this.querySelector('[data-add-button]');
          this.addLabel = this.querySelector('[data-add-label]');
          this.sticky = this.querySelector('[data-sticky]');

          this.querySelectorAll('[data-size-input]').forEach((input) =>
            input.addEventListener('change', () => this.setVariant(Number(input.value)))
          );
          this.querySelectorAll('[data-qty-step]').forEach((button) =>
            button.addEventListener('click', () => this.setQuantity(this.quantity + Number(button.dataset.qtyStep)))
          );
          this.qtyInput?.addEventListener('change', () => this.setQuantity(this.quantity));

          // The subscription widget sets its selling_plan field a moment after a click, without an event:
          // re-read on the next frame and once more shortly after any interaction or DOM change.
          const schedule = () => {
            cancelAnimationFrame(this.frame);
            clearTimeout(this.lateUpdate);
            this.frame = requestAnimationFrame(() => this.update());
            this.lateUpdate = setTimeout(() => this.update(), 300);
          };
          this.addEventListener('change', schedule);
          this.addEventListener('click', schedule);
          if (this.form) {
            new MutationObserver(schedule).observe(this.form, {
              subtree: true,
              childList: true,
              attributes: true,
              attributeFilter: ['value', 'checked', 'disabled'],
            });
          }

          // The sticky button is a submit button of the form itself (see the section), no click handler needed.
          this.updateStickyVisibility = () => {
            if (!this.sticky) return;
            const visible = mobileQuery.matches;
            this.sticky.setAttribute('aria-hidden', String(!visible));
            this.sticky.querySelector('[data-sticky-add]')?.setAttribute('tabindex', visible ? '0' : '-1');
          };
          mobileQuery.addEventListener('change', this.updateStickyVisibility);
          this.updateStickyVisibility();

          // Theme previews only: Shopify's preview bar sits at the bottom of the screen, over the sticky bar.
          // Lift ours above it so the Add to cart button can be tested. Customers never get the preview bar.
          const liftAbovePreviewBar = () => {
            if (this.sticky && document.getElementById('PBarNextFrame')) this.sticky.style.bottom = '60px';
          };
          liftAbovePreviewBar();
          setTimeout(liftAbovePreviewBar, 1500);

          // The page preselects the smallest size, but the Joy subscription widget starts on the product's
          // first variant. Once it has loaded, tell it which size is selected (it listens for this change).
          if (!new URL(window.location.href).searchParams.get('variant')) {
            const announce = () => {
              const checked = this.querySelector('[data-size-input]:checked');
              if (checked) this.notifySubscriptions(checked.value);
            };
            setTimeout(announce, 300);
            setTimeout(announce, 1500);
          }

          this.initGallery();
          this.update();
        }

        disconnectedCallback() {
          mobileQuery.removeEventListener('change', this.updateStickyVisibility);
        }

        get variant() {
          const id = Number(this.variantInput?.value);
          return this.data.variants.find((v) => v.id === id) || this.data.variants[0];
        }

        get quantity() {
          const value = parseInt(this.qtyInput?.value, 10);
          return Number.isFinite(value) && value > 0 ? value : 1;
        }

        get sellingPlanId() {
          if (!this.form) return null;
          const fromForm = new FormData(this.form).get('selling_plan');
          if (fromForm) return String(fromForm);
          const checked = this.querySelector('[name="selling_plan"]:checked, select[name="selling_plan"]');
          return checked && checked.value ? String(checked.value) : null;
        }

        setVariant(id) {
          if (!this.variantInput) return;
          this.variantInput.value = id;
          const url = new URL(window.location.href);
          url.searchParams.set('variant', id);
          window.history.replaceState({}, '', url.toString());
          this.notifySubscriptions(id);
          this.update();
        }

        // The Joy subscription widget updates its prices as soon as it sees a request with ?variant= (that is
        // how it follows Dawn's own variant picker). A tiny request for this product's JSON does that at once,
        // instead of waiting for its 800 ms fallback timer.
        notifySubscriptions(id) {
          const path = window.location.pathname.replace(/\/$/, '');
          fetch(`${path}.js?variant=${id}`, { credentials: 'same-origin' }).catch(() => {});
        }

        setQuantity(value) {
          if (!this.qtyInput) return;
          const max = parseInt(this.qtyInput.max, 10) || 99;
          this.qtyInput.value = Math.min(Math.max(1, value), max);
          this.update();
        }

        sizeLabel(variant) {
          if (!this.data.usesTins) return variant.title;
          const template = variant.tins === 1 ? this.data.strings.tinsOne : this.data.strings.tinsOther;
          return fill(template, { __N__: variant.tins });
        }

        update() {
          const variant = this.variant;
          const quantity = this.quantity;
          const plan = this.sellingPlanId;
          const unitPrice = plan && variant.plans[plan] != null ? variant.plans[plan] : variant.price;
          const total = this.formatMoney(unitPrice * quantity);
          const { strings } = this.data;

          let label;
          if (!variant.available) {
            label = strings.soldOut;
          } else if (this.data.usesTins && variant.tins > 1) {
            const tins = fill(strings.tinsOther, { __N__: variant.tins * quantity });
            label = fill(strings.addTins, { __T__: tins, __P__: total });
          } else {
            label = fill(strings.addN, { __N__: quantity, __P__: total });
          }

          if (this.addLabel && !this.addButton?.classList.contains('loading')) this.addLabel.textContent = label;
          if (this.addButton) this.addButton.disabled = !variant.available;

          const summary = this.querySelector('[data-sticky-summary]');
          const stickyTotal = this.querySelector('[data-sticky-total]');
          const stickyButton = this.querySelector('[data-sticky-add]');
          if (summary) summary.textContent = fill(strings.qtySummary, { __N__: quantity, __S__: this.sizeLabel(variant) });
          if (stickyTotal) stickyTotal.textContent = total;
          if (stickyButton) {
            stickyButton.disabled = !variant.available;
            stickyButton.textContent = variant.available ? strings.addToCart : strings.soldOut;
          }
        }

        initGallery() {
          const track = this.querySelector('[data-gallery-track]');
          if (!track) return;
          const slides = [...track.querySelectorAll('[data-gallery-slide]')];
          const dots = [...this.querySelectorAll('[data-gallery-dot]')];
          const thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];

          const setActive = (index) => {
            slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
            dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
            thumbs.forEach((thumb, i) => {
              thumb.classList.toggle('is-active', i === index);
              if (i === index) thumb.setAttribute('aria-current', 'true');
              else thumb.removeAttribute('aria-current');
            });
          };

          thumbs.forEach((thumb) =>
            thumb.addEventListener('click', () => setActive(Number(thumb.dataset.galleryThumb)))
          );

          let ticking = false;
          track.addEventListener(
            'scroll',
            () => {
              if (ticking || !mobileQuery.matches) return;
              ticking = true;
              requestAnimationFrame(() => {
                ticking = false;
                const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
                setActive(Math.min(Math.max(index, 0), slides.length - 1));
              });
            },
            { passive: true }
          );
        }
      }
    );
  }

  // Accordions: [data-kk-accordions="static-desktop"] are open, non-collapsible columns on desktop;
  // on mobile only [data-mobile-open] starts open. Plain [data-kk-accordions] stay accordions everywhere,
  // with [data-desktop-open] open on desktop.
  const applyAccordions = () => {
    const mobile = mobileQuery.matches;
    document.querySelectorAll('[data-kk-accordions]').forEach((container) => {
      const staticDesktop = container.dataset.kkAccordions === 'static-desktop';
      container.querySelectorAll('[data-kk-acc]').forEach((details) => {
        if (mobile) details.open = details.hasAttribute('data-mobile-open');
        else details.open = staticDesktop || details.hasAttribute('data-desktop-open');
      });
    });
  };

  document.addEventListener('click', (event) => {
    const summary = event.target.closest('[data-kk-accordions="static-desktop"] [data-kk-acc] > summary');
    if (summary && !mobileQuery.matches) event.preventDefault();
  });

  // Goes with: add the selected product and the cross-sell product in one request.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-kk-add-both]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    const main = document.querySelector('kookut-product');
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const items = [];
    if (main?.variant) {
      const item = { id: main.variant.id, quantity: main.quantity };
      if (main.sellingPlanId) item.selling_plan = main.sellingPlanId;
      items.push(item);
    }
    items.push({ id: Number(button.dataset.kkAddBoth), quantity: 1 });

    button.setAttribute('aria-disabled', 'true');
    try {
      const body = { items };
      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      const response = await fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      const errorEl = button.parentElement.querySelector('[data-kk-add-both-error]');
      if (errorEl) errorEl.hidden = true;
      if (result.status) {
        if (errorEl) {
          errorEl.textContent = result.description || result.message;
          errorEl.hidden = false;
        }
        return;
      }
      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kookut-add-both', cartData: result });
      }
      cart.classList.remove('is-empty');
      cart.renderContents(result);
    } catch (error) {
      console.error(error);
    } finally {
      button.removeAttribute('aria-disabled');
    }
  });

  // Recipe switcher: show the hovered or focused recipe name, back to the current one on leave.
  document.querySelectorAll('[data-kk-recipes]').forEach((switcher) => {
    const label = switcher.querySelector('[data-kk-recipe-name]');
    if (!label) return;
    const current = label.textContent;
    const show = (event) => {
      const tile = event.target.closest('[data-kk-recipe]');
      if (tile) label.textContent = tile.dataset.kkRecipe;
    };
    const reset = () => (label.textContent = current);
    switcher.addEventListener('mouseover', show);
    switcher.addEventListener('focusin', show);
    switcher.addEventListener('mouseleave', reset);
    switcher.addEventListener('focusout', reset);

    // Scroll each swipe row so the current recipe is in view (on load, and when the compact grid opens).
    const centreCurrent = () => {
      const tile = switcher.querySelector('.kk-recipe.is-current');
      const row = tile?.closest('.kk-recipes__list');
      if (!row || row.scrollWidth <= row.clientWidth) return;
      row.scrollLeft = tile.offsetLeft - row.offsetLeft - (row.clientWidth - tile.offsetWidth) / 2;
    };
    centreCurrent();
    switcher.addEventListener('toggle', () => switcher.open && centreCurrent());
  });

  mobileQuery.addEventListener('change', applyAccordions);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applyAccordions);
  else applyAccordions();
})();
